import React, { useEffect, useRef, useState } from "react";
import classes from "./Newitem.module.css";
import actionStyles from "../ActionPage/ActionPage.module.css";
import HeaderNav from "../../Components/Dahboard Header/HeaderNav/HeaderNav";
import Footer from "../../Components/Footer/Footer";
import { useLocation } from "react-router-dom";
import { Trash2 } from "lucide-react";
import Select from "react-select";
import axios from "axios";
import swal from "sweetalert";
import { useNavigate } from "react-router-dom";
import api, { withCompanyId } from "../../Components/API/Api";
import SupplierModal from '../Suppliers/SupplierModal';
import { normalizeAutoVatSetting, shouldDisableVatCheckbox } from '../Settings/organizationSettingsUtils';

const NewItem = ({ invoice: invoiceProp, onClose, embedded = false }) => {
  const location = useLocation();
  const [type, setType] = useState("products"); // "products" or "services"
  const [productOptions, setProductOptions] = useState([]);
  const [serviceOptions, setServiceOptions] = useState([]);
  const [products, setProducts] = useState([]);
  const [invoiceDate, setInvoiceDate] = useState("");
  const [dueDate, setDueDate] = useState("");
  const [invoiceCode, setInvoiceCode] = useState("");
  const [services, setServices] = useState([]);
  const [currencyOptions, setCurrencyOptions] = useState([]);
  const [selectedCurrency, setSelectedCurrency] = useState(null);
  const [customerOptions, setCustomerOptions] = useState([]);
  const [showSupplierModal, setShowSupplierModal] = useState(false);
  const [selectedAccount, setSelectedAccount] = useState(null);
  const [currencies, setCurrencies] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedCustomerId, setSelectedCustomerId] = useState(null);
  const [taxOptions, setTaxOptions] = useState([]);
  const [selectedTax, setSelectedTax] = useState(null); // Selected VAT rate (company tax)
  const [applyVAT, setApplyVAT] = useState(false); // Toggle VAT on/off
  const [autoVatDeduction, setAutoVatDeduction] = useState(false);
  const [accountOptions, setAccountOptions] = useState([]);
  const [currency, setCurrency] = useState("NGN");
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const editInvoice = invoiceProp || (location.state?.mode === "edit" ? location.state.invoice : null);
  const isEditMode = Boolean(editInvoice);
  const prefilledInvoice = useRef(null);
  
  const formatMoney = (value) => {
    const raw = String(value || '').replace(/,/g, ''); // remove commas
    if (!raw) return '';
    const number = parseFloat(raw);
    if (isNaN(number)) return '';
    return number.toLocaleString();
  };

  useEffect(() => {
    if (!editInvoice || prefilledInvoice.current === editInvoice) return;
    prefilledInvoice.current = editInvoice;

    const invoiceValue = (...values) => values.find(value => value !== undefined && value !== null && value !== "");
    const toSelectId = (value) => {
      const id = typeof value === "object" && value !== null ? value.id ?? value.value : value;
      if (id === undefined || id === null || id === "") return "";
      return Number.isNaN(Number(id)) ? id : Number(id);
    };
    const getId = (value) => toSelectId(value);

    const optionType = String(editInvoice.option_type || editInvoice.type || "").toLowerCase();
    const invoiceType = optionType.includes("service") ? "services" : "products";
    setType(invoiceType);

    const supplier = editInvoice.supplier || editInvoice.beneficiary || {};
    const supplierId = getId(invoiceValue(editInvoice.supplier_id, supplier.id, supplier.value));
    const supplierOption = customerOptions.find(option => String(option.value) === String(supplierId));
    setSelectedCustomer(supplierOption || (supplierId !== "" ? {
      value: supplierId,
      id: supplierId,
      label: supplier.name || editInvoice.supplier_name || String(supplierId),
    } : null));
    setSelectedCustomerId(supplierId || null);

    const currencyValue = editInvoice.currency_id ?? editInvoice.currency?.id ?? editInvoice.currency?.value ?? editInvoice.currency;
    const currencyOption = currencyOptions.find(option =>
      String(option.value) === String(currencyValue) ||
      String(option.code) === String(currencyValue)
    );
    setSelectedCurrency(currencyOption || (currencyValue ? {
      value: toSelectId(currencyValue),
      label: editInvoice.currency?.name || editInvoice.currency?.code || String(currencyValue),
      code: editInvoice.currency?.code || (typeof currencyValue === "string" ? currencyValue : undefined),
    } : null));

    setInvoiceDate(String(invoiceValue(editInvoice.transaction_date, editInvoice.invoice_date, "")).split("T")[0]);
    setDueDate(String(invoiceValue(editInvoice.due_date, "")).split("T")[0]);
    setInvoiceCode(String(invoiceValue(editInvoice.invoice_number, editInvoice.document_number, "")));

    const lineCollection = [
      editInvoice.items,
      editInvoice.invoice_items,
      editInvoice.purchase_invoice_items,
      editInvoice.purchase_items,
      editInvoice.lines,
    ].find(Array.isArray);
    const arrayFields = ["item_id", "quantity", "item_amount", "reference", "tax_id", "wht_id", "discount", "discount_percentage"];
    const lineCount = Math.max(
      lineCollection?.length || 0,
      ...arrayFields.map(field => Array.isArray(editInvoice[field]) ? editInvoice[field].length : 0)
    );
    const lines = lineCollection || Array.from({ length: lineCount }, (_, index) =>
      Object.fromEntries(arrayFields.map(field => [field, editInvoice[field]?.[index]]))
    );

    if (lines.length > 0) {
      setInvoiceItems(lines.map((line, index) => {
        const quantity = Number(invoiceValue(line.quantity, line.qty, 1)) || 1;
        const lineAmount = Number(invoiceValue(line.item_amount, line.line_total, line.amount, 0)) || 0;
        const unitPrice = Number(invoiceValue(
          line.unit_price,
          line.price,
          line.rate,
          line.item?.price,
          quantity ? lineAmount / quantity : lineAmount
        )) || 0;
        const discountPercentage = Number(invoiceValue(line.discount_percentage, line.discountPercent, 0)) || 0;
        const discountAmount = Number(invoiceValue(line.discount_amount, line.discount, 0)) || 0;
        const itemId = getId(invoiceValue(line.item_id, line.product_id, line.stock_id, line.item?.id, line.product?.id, line.ledger));
        const taxId = invoiceValue(line.tax_id, line.vat_id);
        const whtId = invoiceValue(line.wht_id, line.withholding_tax_id);

        return {
          ledger: itemId,
          account: getId(invoiceValue(line.account_to_debit, line.account_id, line.account)),
          description: String(invoiceValue(line.reference, line.description, line.item?.name, line.product?.name, "")),
          qty: quantity,
          price: unitPrice,
          discountRaw: discountPercentage > 0 ? `${discountPercentage}%` : (discountAmount ? String(discountAmount) : ""),
          discountAmount,
          vatChecked: taxId !== undefined && taxId !== null && taxId !== "" && Number(taxId) !== 0,
          tax_id: taxId || null,
          wthChecked: whtId !== undefined && whtId !== null && whtId !== "" && Number(whtId) !== 0,
          vatRate: "Premium",
          vat: Number(line.vat ?? line.tax_amount ?? 0) || 0,
          total: Number(invoiceValue(line.total, line.total_price, line.amount, lineAmount)) || 0,
          key: line.id ?? index,
        };
      }));
    }
  }, [editInvoice, customerOptions, currencyOptions]);

  // shared rounding helper (2 decimals)
  const round2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

  const [invoiceItems, setInvoiceItems] = useState([
    {
      ledger: '',
      description: '',
      account: '',
      vatChecked: autoVatDeduction,
      tax_id: 1,
      qty: 1,
      price: 0,
      // replaced numeric discount with raw input and computed amount
      discountRaw: '',
      discountAmount: 0,
      vatRate: 'Premium',
      vat: 0,
      total: 0,
    }
  ]);
  const [loading, setLoading] = useState(true);
  const isMounted = useRef(true);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  useEffect(() => {
    const fetchCompanySettings = async () => {
      try {
        const companyId = localStorage.getItem('company_id') || localStorage.getItem('activeCompanyId');
        if (!companyId) return;
        const res = await api.get('/v1/companies/get-info', {
          headers: { company_id: companyId },
        });
        const items = Array.isArray(res?.data?.data) ? res.data.data : Array.isArray(res?.data) ? res.data : [];
        const company = Array.isArray(items) && items.length ? (items.find((item) => String(item.id) === String(companyId)) || items[0]) : null;
        if (company) {
          const normalized = normalizeAutoVatSetting(company.auto_vat_deduction);
          setAutoVatDeduction(normalized);

          if (normalized) {
            setInvoiceItems(prev => prev.map((row) => ({
              ...row,
              vatChecked: true,
              tax_id: row.tax_id || 1,
            })));
          }
        }
      } catch (err) {
        console.error('Failed to fetch company VAT setting', err);
      }
    };

    fetchCompanySettings();
  }, []);

  // Recalculate all line items' VAT and totals when VAT toggle or selected rate changes
  useEffect(() => {
    // Recompute VAT/Totals per-row using each row's vatChecked and tax_id (or selectedTax)
    setInvoiceItems(prevItems => {
      return prevItems.map(item => {
        const qtyNumber = Number(item.qty) || 0;
        const priceNumber = Number(item.price) || 0;
        const discount = Number(item.discountAmount) || 0; // use computed amount
        const baseTotal = round2(qtyNumber * priceNumber);
        // determine tax rate for this row only if vatChecked is true
        let rate = 0;
        if (item.vatChecked) {
          const taxSource = item.tax_id || (selectedTax && selectedTax.value) || (taxOptions && taxOptions.length ? taxOptions[0].value : null);
          if (taxSource != null) {
            const t = taxOptions.find(tx => Number(tx.value) === Number(taxSource));
            if (t && t.rate != null) {
              const parsed = parseFloat(t.rate);
              rate = isNaN(parsed) ? 0 : parsed / 100;
            }
          }
        }
        const vatAmount = round2((baseTotal - discount) * rate);
        const total = round2(baseTotal - discount + vatAmount);
        return {
          ...item,
          vat: vatAmount,
          total,
        };
      });
    });
  }, [applyVAT, selectedTax, taxOptions]);

  useEffect(() => {
    const fetchTaxes = async () => {
      try {
        const res = await api.get("/v1/taxes/company-tax");
        const options = Array.isArray(res.data.data)
          ? res.data.data.map((tax) => ({
            value: tax.id,
            label: `${tax.description} (${tax.rate}%)`,
            rate: tax.rate, // if available
          }))
          : [];
        setTaxOptions(options);
        // Do not force-select a rate; if user enables VAT without choosing, we'll use first option
      } catch (err) {
        setTaxOptions([]);
      }
    };
    fetchTaxes();
    
  }, []);

  useEffect(() => {
    const fetchAccounts = async () => {
      try {
        const res = await api.get("/v1/get-fixedandexpense-accounts");
        const options = Array.isArray(res.data.data)
          ? res.data.data.map(item => ({
            // use numeric id as Select value so selectedAccount.value is numeric
            value: item.id,
            id: item.id,
            gl_name: item.gl_name,
            label: item.account_name || item.name || item.description || String(item.gl_name || item.id),
          }))
          : [];
        setAccountOptions(options);
      } catch (err) {
        setAccountOptions([]);
      }
    };
    fetchAccounts();
  }, []);

  // useEffect(() => {
  //   const fetchInvoices = async () => {
  //     try {
  //       const token = localStorage.getItem("authToken");
  //       const res = await axios.post(`${BASE_URL}/v1/post-sales-invoice`, {
  //         headers: {
  //           Authorization: `Bearer ${token}`,
  //           Accept: "application/json",
  //         },
  //       });

  //       const data = res.data?.data || [];
  //       const formatted = data.map(item => ({
  //         ledger: item.ledger || '',
  //         description: item.description || '',
  //         qty: item.qty || 0,
  //         price: item.price || 0,
  //         discount: item.discount || 0,
  //         vatRate: item.vatRate || 'Premium',
  //         vat: item.vat || 0,
  //         total: item.total || 0,
  //       }));

  //       setInvoiceItems(formatted);
  //     } catch (error) {
  //       console.error("Error loading invoices:", error);
  //     } finally {
  //       setLoading(false);
  //     }
  //   };

  //   fetchInvoices();
  // }, []);


  // Fetch invoice code on mount
  useEffect(() => {
    if (isEditMode) return;

    const fetchInvoiceCode = async () => {
      try {
        const res = await api.get("/v1/generate-sales-invoice-code");
        setInvoiceCode(res.data?.data?.toString() || "");
      } catch (err) {
        setInvoiceCode("");
      }
    };
    fetchInvoiceCode();
  }, [isEditMode]);

  const regenerateInvoiceCode = async () => {
    
    try {
      const res = await api.get("/v1/generate-sales-invoice-code");
      setInvoiceCode(res.data?.data?.toString() || "");
    } catch (err) {
      console.error('Failed to regenerate invoice code', err?.response || err.message || err);
    }
  };

  // const handleChange = (index, field, value) => {
  //   const updatedItems = [...invoiceItems];
  //   const item = { ...updatedItems[index], [field]: field === 'ledger' || field === 'vatRate' || field === 'description' ? value : parseFloat(value) || 0 };

  //   const baseTotal = item.qty * item.price;
  //   const discount = item.discount || 0;
  //   const vatAmount = (baseTotal - discount) * 0.075;

  //   item.vat = vatAmount;
  //   item.total = baseTotal - discount + vatAmount;

  //   updatedItems[index] = item;
  //   setInvoiceItems(updatedItems);
  // };

  const handleAddRow = () => {
    setInvoiceItems(prev => [
      ...prev,
      {
        ledger: '',
        account: '',
        description: '',
        vatChecked: autoVatDeduction,
        tax_id: 1,
        qty: 1,
        price: 0,
        // replaced numeric discount with raw input and computed amount
        discountRaw: '',
        discountAmount: 0,
        vatRate: 'Premium',
        vat: 0,
        total: 0,
      }
    ]);
  };

  const handleDeleteRow = (index) => {
    const newItems = [...invoiceItems];
    newItems.splice(index, 1);
    setInvoiceItems(newItems);
  };

  // const handleSaveInvoice = async () => {
  //   try {
  //     const token = localStorage.getItem("authToken");
  //     await axios.post(`${BASE_URL}/api/v1/post-sales-invoice`, {
  //       invoice_data: invoiceItems,
  //       currency: currency,
  //     }, {
  //       headers: {
  //         Authorization: `Bearer ${token}`,
  //         Accept: "application/json",
  //         "Content-Type": "application/json",
  //       },
  //     });

  //     alert("Invoice saved successfully!");
  //   } catch (error) {
  //     console.error("Error saving invoice:", error);
  //     alert("Failed to save invoice");
  //   }
  // };

  const subtotal = invoiceItems.reduce((acc, item) => acc + (Number(item.qty) || 0) * (Number(item.price) || 0), 0);
  const totalVAT = invoiceItems.reduce((acc, item) => acc + (Number(item.vat) || 0), 0);
  const totalDiscount = invoiceItems.reduce((acc, item) => acc + (Number(item.discountAmount) || 0), 0);
  const totalWHT = invoiceItems.reduce((acc, item) => {
    const qty = Number(item.qty) || 0;
    const price = Number(item.price) || 0;
    const discount = Number(item.discountAmount) || 0;
    const taxable = Math.max(0, qty * price - discount);
    const wht = item.wthChecked ? taxable * 0.02 : 0;
    return acc + wht;
  }, 0);
  const grandTotal = invoiceItems.reduce((acc, item) => acc + (Number(item.total) || 0), 0);

  // overall discount percentage (relative to subtotal) — shown in summary when calculable
  const discountPercentageOverall = subtotal > 0 ? round2((totalDiscount / subtotal) * 100) : null;

  const pageMap = {
    "/dashboard": "Dashboard",
    "/transactions": "Transactions",
    "/accounts": "Accounting",
    "/reports": "Reports",
    "/banking": "Banking",
    "/settings": "Settings",
  };

  // Fetch products
  // Fetch products
  const fetchProducts = async () => {
    try {
        const res = await api.get("/v1/fetch_stocks");
      const options = Array.isArray(res.data.data)
        ? res.data.data.map((item) => ({
          value: item.id,
          label: item.name,
          price: item.price,   // ✅ use price
        }))
        : [];
      setProductOptions(options);
    } catch (err) {
      setProductOptions([]);
    }
  };

  // Fetch services
  const fetchServices = async () => {
    try {
        const res = await api.get("/v1/items/fetch-services");
      const options = Array.isArray(res.data.data)
        ? res.data.data.map((item) => ({
          value: item.id,
          label: item.name,
          price: item.price,   // ✅ use price
        }))
        : [];
      setServiceOptions(options);
    } catch (err) {
      setServiceOptions([]);
    }
  };


  // Fetch on type change
  useEffect(() => {
    if (type === "products") fetchProducts();
    else fetchServices();
  }, [type]);


  // Simulate fetching data (replace with real API calls)
  // const fetchProducts = async () => {
  // Replace with your API call
  //   setProducts([
  //     { name: "Product A", price: 100 },
  //     { name: "Product B", price: 200 },
  //   ]);
  // };
  // const fetchServices = async () => {
  // Replace with your API call
  //   setServices([
  //     { name: "Service A", price: 150 },
  //     { name: "Service B", price: 250 },
  //   ]);
  // };

  // Fetch data when type changes
  // React.useEffect(() => {
  //   if (type === "products") fetchProducts();
  //   else fetchServices();
  // }, [type]);

  // Fetch currencies for the Currency select
  useEffect(() => {
    const fetchCurrencies = async () => {
      try {
        const res = await api.get("/v1/fetch-all-currencies");

        const options = Array.isArray(res.data.data)
          ? res.data.data.map((item) => ({
            value: item.id, // only id
            label: item.name || item.code,
            code: item.code, // keep code so we can check NGN
          }))
          : [];

        setCurrencyOptions(options);

        // ✅ set NGN as default if exists
        const ngnOption = options.find(opt => opt.code === "NGN");
        if (ngnOption) {
          setSelectedCurrency(ngnOption);
        }

      } catch (err) {
        setCurrencyOptions([]);
      }
    };

    fetchCurrencies();
  }, []);
  // Fetch customers/suppliers for the select. Extracted so it can be re-used after creating a new supplier.
  const fetchCustomers = async () => {
    try {
        const res = await api.get("/v1/beneficiary");

      // Normalize response shape
      const customerData = res.data?.data?.data || res.data?.data || [];
      const options = Array.isArray(customerData)
        ? customerData.map(item => ({ value: item.id, label: item.name, id: item.id }))
        : [];
      setCustomerOptions(options);
    } catch (err) {
      console.error('Failed to fetch customers', err?.response || err.message || err);
      setCustomerOptions([]);
    }
  };

  useEffect(() => {
    fetchCustomers();
  }, []);

  const handleChange = (index, field, value) => {
    const updatedItems = [...invoiceItems];
    let item = { ...updatedItems[index] };

    if (field === 'qty') {
      if (value === '') {
        item.qty = '';
      } else {
        const num = Number(value);
        item.qty = num >= 0 ? num : 0;
      }
    } else if (field === 'price') {
      const num = parseFloat(value);
      item.price = isNaN(num) ? 0 : num;
    } else if (field === 'discount') {
      // Accept either a numeric amount or a percentage string like "10%".
      // Also auto-detect plain numbers intended as percentages (e.g. user types "5" meaning 5%).
      const raw = String(value || '').trim();
      let discAmt = 0;
      const qtyNumber = Number(item.qty) || 0;
      const priceNumber = Number(item.price) || 0;
      const baseTotal = round2(qtyNumber * priceNumber);

      if (raw.endsWith('%')) {
        const pct = parseFloat(raw.slice(0, -1));
        const percent = isNaN(pct) ? 0 : pct;
        discAmt = round2((baseTotal * percent) / 100);
        item.discountRaw = raw;
        item.discountAmount = discAmt;
      } else {
        const cleaned = raw.replace(/,/g, '');
        const num = parseFloat(cleaned);

        // Heuristic: if entered number is between 0 and 100 and relatively small
        // compared to the line total, interpret as percentage.
        if (!isNaN(num) && baseTotal > 0 && num > 0 && num <= 100 && num < (baseTotal * 0.01)) {
          const pct = num;
          discAmt = round2((baseTotal * pct) / 100);
          item.discountRaw = `${pct}%`;
          item.discountAmount = discAmt;
        } else {
          discAmt = isNaN(num) ? 0 : round2(num);
          item.discountRaw = raw;
          item.discountAmount = discAmt;
        }
      }
    } else {
      item[field] = value;
    }

    // If qty or price changed and discount was entered as percentage, recompute discountAmount
    if ((field === 'qty' || field === 'price') && item.discountRaw && String(item.discountRaw).trim().endsWith('%')) {
      const pct = parseFloat(String(item.discountRaw).trim().slice(0, -1));
      const percent = isNaN(pct) ? 0 : pct;
      const qtyNumberLocal = Number(item.qty) || 0;
      const priceNumberLocal = Number(item.price) || 0;
      const baseTotalLocal = round2(qtyNumberLocal * priceNumberLocal);
      item.discountAmount = round2((baseTotalLocal * percent) / 100);
    }

    const qtyNumber = Number(item.qty) || 0;
    const priceNumber = Number(item.price) || 0;
    const discountAmt = Number(item.discountAmount) || 0;
    const baseTotal = round2(qtyNumber * priceNumber);
    const taxable = Math.max(0, baseTotal - discountAmt);

    // Per-row VAT: 7.5% only when vatChecked is true
    const vatRate = item.vatChecked ? 0.075 : 0;
    const vatAmount = round2(taxable * vatRate);

    // Per-row WHT: 2% of taxable when wthChecked is true
    const whtAmount = item.wthChecked ? round2(taxable * 0.02) : 0;

    const total = round2(taxable - vatAmount - whtAmount);

    item.vat = vatAmount;
    item.total = total;

    updatedItems[index] = item;
    setInvoiceItems(updatedItems);
  };


  const handleSaveInvoice = async () => {
    if (isEditMode) {
      swal("Unavailable", "Updating purchase invoices is not available yet.", "info");
      return;
    }

    // Basic validation (optional, you can expand this)
    if (
      !type ||
      !selectedCustomer ||
      !invoiceCode ||
      !selectedCurrency ||
      !invoiceDate ||
      !dueDate ||
      invoiceItems.length === 0
    ) {
      swal("Error", "Please fill all required fields.", "error");
      return;
    }

    // Validate each item: require `ledger` for products
    const hasInvalidItem = invoiceItems.some(item => {
      const missingItem = type === 'products' ? !item.ledger : false;
      return missingItem || !item.qty || Number(item.qty) <= 0;
    });
    if (hasInvalidItem) {
      swal("Error", "Each row must have the correct selection (product or service name) and quantity greater than 0.", "error");
      return;
    }

    // Account selection is now optional - backend will handle
    // const allRowsHaveAccount = invoiceItems.every(row => row.account && String(row.account).trim() !== '');
    // if (!selectedAccount && !allRowsHaveAccount) {
    //   swal("Error", "Please select an Account (either choose one above or select an account for each row).", "error");
    //   return;
    // }

    setIsSubmitting(true);

    // Build aligned per-line arrays (follow sales invoice pattern)
    const item_id = [];
    // const account_to_debit = [];
    const quantity = [];
    const item_amount = []; // lineAmount = qty * price
    const tax_id = [];
    const wht_id = [];
    const discount_arr = [];
    const discount_percentage = [];
    const reference = [];

    let sub_total_calc = 0; // sum of line amounts (qty * price) BEFORE discount
    let total_discount_calc = 0;
    let total_vat_calc = 0;
    let total_wht_calc = 0; // sum of per-row WHT (computed on line taxable)

    invoiceItems.forEach(row => {
      // Use full precision for base math, then round for submission.
      const qty = Number(row.qty) || 0;
      const price = Number(row.price) || 0;
      const rawLineAmount = qty * price; // unrounded
      const lineAmount = round2(rawLineAmount); // rounded for display/submission

      // Determine discount amount and percentage precisely (use rounded lineAmount consistently)
      let discountAmt = 0;
      let pct = 0;
      if (row.discountRaw && String(row.discountRaw).trim().endsWith('%')) {
        // user entered percentage -> send exact percentage and compute amount from raw line amount
        const parsed = parseFloat(String(row.discountRaw).trim().slice(0, -1));
        pct = isNaN(parsed) ? 0 : parsed; // send as entered (e.g. 10)
        discountAmt = +(rawLineAmount * (pct / 100)).toFixed(2); // compute using raw (unrounded) line amount
      } else {
        // user entered absolute amount -> try to detect if they meant a percentage
        let entered = round2(Number(row.discountAmount) || 0);
        if (rawLineAmount > 0 && entered > 0 && entered <= 100 && entered < (rawLineAmount * 0.01)) {
          // likely a percent entered as plain number (e.g. '5' -> 5%)
          pct = entered;
          discountAmt = +(rawLineAmount * (pct / 100)).toFixed(2);
        } else {
          // Preserve entered discount amount even when line amount is zero (important for services)
          discountAmt = entered;
          // compute percentage from raw amount with high precision when possible
          pct = rawLineAmount > 0 ? Number(((entered / rawLineAmount) * 100).toFixed(6)) : null;
        }
      }

      const lineAmountRaw = rawLineAmount;
      const taxable = Math.max(0, lineAmountRaw - discountAmt);
      // Per-row VAT at fixed 7.5% when vatChecked is true
      const lineVat = row.vatChecked ? taxable * 0.075 : 0;
      const lineWht = row.wthChecked ? (taxable * 0.02) : 0;

      // For products send numeric ledger id
      item_id.push(row.ledger ? Number(row.ledger) : null);
      // reference: use description as reference
      reference.push(String(row.description || ""));
      // account_to_debit: use selectedAccount.id (numeric) if available, otherwise try to coerce
      // let acctId = null;
      // if (selectedAccount && selectedAccount.id != null) acctId = Number(selectedAccount.id);
      // else if (selectedAccount && !isNaN(Number(selectedAccount.value))) acctId = Number(selectedAccount.value);
      // else if (row.account && !isNaN(Number(row.account))) acctId = Number(row.account);
      // account_to_debit.push(acctId == null ? '' : acctId);
      quantity.push(qty);
      item_amount.push(Number(lineAmount.toFixed(2)));

      // ensure tax_id array contains 1 when VAT is checked (matches previous behaviour)
      tax_id.push(row.vatChecked ? 1 : null);
      wht_id.push(row.wthChecked ? 2 : null);

      // send numeric values with stable formatting
      // follow MakeSale: send null when no discount, amount rounded to 2dp when present
      discount_arr.push(discountAmt > 0 ? Number(discountAmt.toFixed(2)) : null);
      // send percentage: match sales page behavior -> percent rounded to 2 decimals or null
      const pctToSend = pct && Number(pct) > 0 ? Number(Number(pct).toFixed(2)) : null;
      discount_percentage.push(pctToSend);

      sub_total_calc = sub_total_calc + lineAmountRaw;
      total_discount_calc = total_discount_calc + discountAmt;
      total_vat_calc = total_vat_calc + lineVat;
      total_wht_calc = total_wht_calc + lineWht;
    });

    // Ensure per-line arrays match invoiceItems length (send as arrays)
    const expectedLen = invoiceItems.length;
    const pad = (arr, len, padVal) => {
      while ((arr || []).length < len) arr.push(padVal);
      return arr;
    };

    pad(item_id, expectedLen, '');
    pad(quantity, expectedLen, 0);
    pad(item_amount, expectedLen, 0);
    pad(tax_id, expectedLen, null);
    pad(wht_id, expectedLen, null);
    pad(discount_arr, expectedLen, null);
    pad(discount_percentage, expectedLen, null);
    pad(reference, expectedLen, '');
    // pad(account_to_debit, expectedLen, '');

    const sub_total = +sub_total_calc.toFixed(2);
    const total_discount = +total_discount_calc.toFixed(2);
    const total_vat = +total_vat_calc.toFixed(2);
    const total_wht = +total_wht_calc.toFixed(2);
    const total_price = +(sub_total - total_discount - total_vat - total_wht).toFixed(2);
    const amount = total_price;

    // Build a JS payload object for logging/debug
    let payloadObj = {
      option_type: type === "products" ? "product" : "service",
      description: invoiceItems.map(item => item.description).join("; "),
      invoice_number: invoiceCode,
      supplier_id: selectedCustomerId || selectedCustomer?.value,
      currency: selectedCurrency.value,
      transaction_date: invoiceDate,
      due_date: dueDate,

      // include item_id for products (if applicable)
      item_id,
      reference,

      // account_to_debit,
      quantity,
      item_amount,
      tax_id,
      wht_id,
      discount: discount_arr,
      discount_percentage,

      sub_total,
      total_discount,
      total_vat,
      total_wht,
      total_price,
      amount,
    };

    // helper: convert payloadObj into FormData with array fields (item_id[], quantity[], ...)
    const buildFormData = (p) => {
      const fd = new FormData();
      const norm = (v, opts) => {
        if (v == null) return '';
        if (typeof v === 'object') {
          if (v.id != null) return String(v.id);
          if (v.value != null) return String(v.value);
          try { return JSON.stringify(v); } catch (e) { return String(v); }
        }
        if (typeof v === 'number') return opts && opts.money ? Number(v).toFixed(2) : String(v);
        return String(v);
      };

      // scalar fields
      fd.append('option_type', norm(p.option_type));
      fd.append('description', norm(p.description || ''));
      fd.append('invoice_number', norm(p.invoice_number || ''));
      fd.append('supplier_id', norm(p.supplier_id || ''));
      fd.append('currency', norm(p.currency || ''));
      fd.append('transaction_date', norm(p.transaction_date || ''));
      fd.append('due_date', norm(p.due_date || ''));

      // arrays: append each element using PHP array notation; always append primitive strings
      (p.item_id || []).forEach(v => fd.append('item_id[]', norm(v)));
      (p.quantity || []).forEach(v => fd.append('quantity[]', norm(v)));
      (p.item_amount || []).forEach(v => fd.append('item_amount[]', norm(v, { money: true })));
      (p.tax_id || []).forEach(v => fd.append('tax_id[]', norm(v)));
      (p.wht_id || []).forEach(v => fd.append('wht_id[]', norm(v)));
      (p.discount || []).forEach(v => fd.append('discount[]', norm(v, { money: true })));
      (p.discount_percentage || []).forEach(v => fd.append('discount_percentage[]', norm(v)));
      (p.reference || []).forEach(v => fd.append('reference[]', norm(v)));
      // (p.account_to_debit || []).forEach(v => fd.append('account_to_debit[]', norm(v)));

      // totals
      fd.append('sub_total', norm(Number(p.sub_total || 0), { money: true }));
      fd.append('total_discount', norm(Number(p.total_discount || 0), { money: true }));
      fd.append('total_vat', norm(Number(p.total_vat || 0), { money: true }));
      fd.append('total_wht', norm(Number(p.total_wht || 0), { money: true }));
      fd.append('total_price', norm(Number(p.total_price || 0), { money: true }));
      fd.append('amount', norm(Number(p.amount || 0), { money: true }));
      return fd;
    };

    let formData = buildFormData(payloadObj);

     // allow a single automatic retry if server returns correct_values
    let autoRetryDone = false;
    let autoJsonTried = false;
 
     // No GL code validation — proceed to submit payload (backend will handle journals)
 
     try {
      const token = localStorage.getItem("authToken");
      // Debug: show payload in console for verification
      console.log("Submitting purchase payload (object):", payloadObj);
      console.log('payloadObj types:', Object.keys(payloadObj).reduce((acc,k)=>{acc[k]=Array.isArray(payloadObj[k])? 'array': typeof payloadObj[k]; return acc},{}) );

      // Send JSON payload (matches backend examples for service/product)
      autoJsonTried = true; // we're sending JSON as primary
      const jsonPayload = {
        option_type: payloadObj.option_type,
        description: payloadObj.description,
        invoice_number: payloadObj.invoice_number,
        transaction_date: payloadObj.transaction_date,
        supplier_id: payloadObj.supplier_id,
        currency: payloadObj.currency,
        due_date: payloadObj.due_date,
        // include arrays exactly as backend expects
        item_id: payloadObj.item_id || [],
        reference: (payloadObj.reference || []).map(v => (v == null ? null : String(v))),
        item_amount: (payloadObj.item_amount || []).map(v => (v == null ? null : Number(v))),
        // account_to_debit: (payloadObj.account_to_debit || []).map(v => (v === '' ? null : Number(v) || null)),
        quantity: (payloadObj.quantity || []).map(v => (v == null ? 0 : Number(v))),
        tax_id: (payloadObj.tax_id || []).map(v => (v == null ? null : Number(v) || null)),
        wht_id: (payloadObj.wht_id || []).map(v => (v == null ? null : Number(v) || null)),
        discount: (payloadObj.discount || []).map(v => (v == null ? null : Number(v) || 0)),
        discount_percentage: (payloadObj.discount_percentage || []).map(v => (v == null ? null : Number(v) || 0)),
        sub_total: Number(payloadObj.sub_total || 0),
        total_discount: Number(payloadObj.total_discount || 0),
        total_vat: Number(payloadObj.total_vat || 0),
        total_wht: Number(payloadObj.total_wht || 0),
        total_price: Number(payloadObj.total_price || 0),
        amount: Number(payloadObj.amount || 0),
      };

      console.log('Submitting JSON payload:', jsonPayload);
      console.log('Making API call to /v1/make-purchase-invoice with timeout 20000ms');
      
      const response = await api.post(
        "/v1/make-purchase-invoice",
        withCompanyId(jsonPayload),
        { timeout: 20000 }
      );
      
      console.log('API response received:', response?.status, response?.data);

      swal("Success", "Invoice saved successfully!", "success")
        .then(() => {
          if (isMounted.current) {
            setIsSubmitting(false);
          }
          // 👇 Redirect and pass invoice details (including totals) to preview page
          navigate("/invoice-preview", {
            state: {
              invoice: {
                invoice_number: invoiceCode,
                transaction_date: invoiceDate,
                due_date: dueDate,
                description: invoiceItems.map(item => item.description).join(", "),
                supplier: {
                  name: selectedCustomer?.label || "—",
                },
                items: invoiceItems,
                // totals – keep in sync with payloadObj / backend
                sub_total,
                total_discount,
                total_vat,
                total_wht,
                total_price,
                total: amount,
                amount,
                balance: amount,
                amount_paid: 0,
              },
            },
          });
        })
        .catch(() => {
          if (isMounted.current) {
            setIsSubmitting(false);
          }
        })
        .finally(() => {
          if (isMounted.current) {
            setIsSubmitting(false);
          }
        });
      // Optionally, reset form here
    } catch (error) {
      // Ensure submit state is cleared so user can retry
      setIsSubmitting(false);
      
      // Log complete error for debugging
      console.error('=== PURCHASE INVOICE SAVE ERROR ===');
      console.error('Error object:', error);
      console.error('Error message:', error?.message);
      console.error('Error code:', error?.code);
      console.error('Response status:', error?.response?.status);
      console.error('Response statusText:', error?.response?.statusText);
      
      const serverData = error.response?.data || {};
      const serverMsg = String(serverData?.message || '');

      console.error('Server data:', serverData);
      console.error('Server message:', serverMsg);
      console.error('=== END ERROR LOG ===');

      // If server reports an Expense Account / Payable GL linkage problem, show it directly
      if (/expense account.*payable\s*GL|does not have a payable\s*GL|payable\s*GL linked/i.test(serverMsg)) {
        try {
          swal('Error', serverMsg, 'error');
        } catch (e) {
          console.error('Failed to show server error message', e);
        }
        return;
      }
      // Log full server response body for easier debugging
      try { console.error('Server response body:', JSON.stringify(serverData, null, 2)); } catch (e) { console.error('Server response (raw):', serverData); }

      // If backend suggests a per-line discount correction (common validation), attempt one automatic retry.
      // This path only runs for true "Discount mismatch" errors and will NOT change other messages
      // like "Expense account 'X' on row 1 does not have a Payable GL linked".
      if (!autoRetryDone && serverData?.message && /Discount mismatch/i.test(serverData.message) && serverData?.data?.correct_discount != null) {
        try {
          const msg = String(serverData.message || '');
          // parse row number like "Discount mismatch for row 1"
          const m = msg.match(/row\s*(\d+)/i);
          const rowIndex = m ? Math.max(0, Number(m[1]) - 1) : 0;

          const correctDiscount = Number(serverData.data.correct_discount);
          const correctPct = Number(serverData.data.correct_discount_percentage);
          console.warn(`Backend requests discount correction for row ${rowIndex + 1}:`, { correctDiscount, correctPct });

          // patch the payload arrays (safely) and rebuild FormData
          if (Array.isArray(payloadObj.discount) && Array.isArray(payloadObj.discount_percentage)) {
            // apply server-suggested discount for the reported row
            payloadObj.discount[rowIndex] = (typeof correctDiscount === 'number') ? Number(correctDiscount) : Number(correctDiscount || 0);
            payloadObj.discount_percentage[rowIndex] = (typeof correctPct === 'number') ? Number(correctPct) : Number(correctPct || 0);

            // Recompute all totals from payload arrays to match backend logic
            try {
              const len = Math.max(
                (payloadObj.item_amount || []).length,
                (payloadObj.quantity || []).length,
                (payloadObj.discount || []).length
              );
              let sub_total_calc = 0;
              let total_discount_calc = 0;
              let total_vat_calc = 0;
              let total_wht_calc = 0;

              for (let i = 0; i < len; i++) {
                const lineAmt = Number((payloadObj.item_amount || [])[i] || 0);
                const disc = (payloadObj.discount || [])[i] == null ? 0 : Number((payloadObj.discount || [])[i] || 0);
                const taxable = Math.max(0, lineAmt - disc);
                const taxId = (payloadObj.tax_id || [])[i];
                const whtId = (payloadObj.wht_id || [])[i];
                const vat = taxId == 1 ? taxable * 0.075 : 0;
                const wht = whtId == 2 ? taxable * 0.02 : 0;

                sub_total_calc += lineAmt;
                total_discount_calc += disc;
                total_vat_calc += vat;
                total_wht_calc += wht;
              }

              payloadObj.sub_total = Number(sub_total_calc.toFixed(2));
              payloadObj.total_discount = Number(total_discount_calc.toFixed(2));
              payloadObj.total_vat = Number(total_vat_calc.toFixed(2));
              payloadObj.total_wht = Number(total_wht_calc.toFixed(2));
              payloadObj.total_price = Number((payloadObj.sub_total - payloadObj.total_discount - payloadObj.total_vat - payloadObj.total_wht).toFixed(2));
              payloadObj.amount = payloadObj.total_price;
            } catch (e) {
              console.error('Failed to recompute totals during discount-correction retry:', e);
            }

            // rebuild FormData and retry once
            formData = buildFormData(payloadObj);
            autoRetryDone = true;

            console.log("Retrying submit after applying backend discount correction:", { rowIndex, payloadObj });
            await api.post("/v1/make-purchase-invoice", withCompanyId(formData), { timeout: 20000 });

            swal("Success", "Invoice saved successfully after applying discount correction!", "success").then(() => {
              if (isMounted.current) setIsSubmitting(false);
              navigate("/invoice-preview", {
                state: {
                  invoice: {
                    invoice_number: invoiceCode,
                    transaction_date: invoiceDate,
                    due_date: dueDate,
                    description: invoiceItems.map(item => item.description).join(", "),
                    supplier: { name: selectedCustomer?.label || "—" },
                    items: invoiceItems,
                    // use corrected totals from payloadObj
                    sub_total: payloadObj.sub_total,
                    total_discount: payloadObj.total_discount,
                    total_vat: payloadObj.total_vat,
                    total_wht: payloadObj.total_wht,
                    total_price: payloadObj.total_price,
                    total: payloadObj.amount,
                    amount: payloadObj.amount,
                    balance: payloadObj.amount,
                    amount_paid: 0,
                  },
                },
              });
            });
            return; // success after retry
          }
        } catch (retryErr) {
          console.error("Discount-correction retry failed:", retryErr.response || retryErr.message || retryErr);
          try { console.error('Discount-correction retry response body:', JSON.stringify(retryErr.response?.data, null, 2)); } catch (e) { console.error('Discount-correction retry response (raw):', retryErr.response?.data); }
          // fall through to existing error display
        }
      }

      // If backend provides correct_values, attempt one automatic retry using FormData
      if (!autoRetryDone && serverData?.data?.correct_values) {
        const cv = serverData.data.correct_values;
        console.warn("Backend correct values:", cv);

        // Update payloadObj totals and rebuild FormData, then retry once
        payloadObj = {
          ...payloadObj,
          sub_total: cv.sub_total,
          total_discount: cv.total_discount,
          total_vat: cv.total_vat,
          total_wht: cv.total_wht,
          total_price: cv.total_price,
          amount: cv.amount,
        };
        formData = buildFormData(payloadObj);
        autoRetryDone = true;

        try {
          console.log("Retrying with server-corrected payload (FormData):", payloadObj);
          await api.post("/v1/make-purchase-invoice", withCompanyId(formData), { timeout: 20000 });

          swal("Success", "Invoice saved successfully after correcting totals!", "success").then(() => {
            if (isMounted.current) {
              setIsSubmitting(false);
            }
            navigate("/invoice-preview", {
              state: {
                invoice: {
                  invoice_number: invoiceCode,
                  invoice_date: invoiceDate,
                  due_date: dueDate,
                  description: invoiceItems.map(item => item.description).join(", "),
                  supplier: { name: selectedCustomer?.label || "—" },
                  items: invoiceItems,
                  // use server‑corrected totals from payloadObj
                  sub_total: payloadObj.sub_total,
                  total_discount: payloadObj.total_discount,
                  total_vat: payloadObj.total_vat,
                  total_wht: payloadObj.total_wht,
                  total_price: payloadObj.total_price,
                  total: payloadObj.amount,
                  amount: payloadObj.amount,
                  balance: payloadObj.amount,
                  amount_paid: 0,
                },
              },
            });
          });
          return; // stop further error handling
        } catch (retryErr) {
          console.error("Retry failed:", retryErr.response || retryErr.message || retryErr);
          try { console.error('Totals-correction retry response body:', JSON.stringify(retryErr.response?.data, null, 2)); } catch (e) { console.error('Totals-correction retry response (raw):', retryErr.response?.data); }
          // fall through to show original message below
        }
      }

      // If server complains about PHP array conversion, retry once with JSON payload
      const msgText = String(serverData?.message || '');
      if (!autoJsonTried && /Array to string conversion/i.test(msgText)) {
        autoJsonTried = true;
        try {
          // Build JSON-friendly payload (primitive arrays, use null for missing)
          const jsonPayload = {
            option_type: payloadObj.option_type,
            description: payloadObj.description,
            invoice_number: payloadObj.invoice_number,
            supplier_id: payloadObj.supplier_id,
            currency: payloadObj.currency,
            transaction_date: payloadObj.transaction_date,
            due_date: payloadObj.due_date,
            item_id: (payloadObj.item_id || []).map(v => (v === '' ? null : Number(v) || null)),
            // account_to_debit: (payloadObj.account_to_debit || []).map(v => (v === '' ? null : Number(v) || null)),
            quantity: (payloadObj.quantity || []).map(v => Number(v) || 0),
            item_amount: (payloadObj.item_amount || []).map(v => Number(v) || 0),
            reference: (payloadObj.reference || []).map(v => String(v || '')),
            tax_id: (payloadObj.tax_id || []).map(v => (v == null ? null : Number(v) || null)),
            wht_id: (payloadObj.wht_id || []).map(v => (v == null ? null : Number(v) || null)),
            discount: (payloadObj.discount || []).map(v => (v == null ? null : Number(v) || 0)),
            discount_percentage: (payloadObj.discount_percentage || []).map(v => (v == null ? null : Number(v) || 0)),
            sub_total: Number(payloadObj.sub_total || 0),
            total_discount: Number(payloadObj.total_discount || 0),
            total_vat: Number(payloadObj.total_vat || 0),
            total_wht: Number(payloadObj.total_wht || 0),
            total_price: Number(payloadObj.total_price || 0),
            amount: Number(payloadObj.amount || 0),
          };

          console.log('Retrying with JSON payload to avoid FormData issues:', jsonPayload);
          await api.post("/v1/make-purchase-invoice", withCompanyId(jsonPayload), { timeout: 20000 });

          swal('Success', 'Invoice saved successfully (JSON retry).', 'success').then(() => {
            if (isMounted.current) setIsSubmitting(false);
            navigate('/invoice-preview', {
              state: {
                invoice: {
                  invoice_number: invoiceCode,
                  transaction_date: invoiceDate,
                  due_date: dueDate,
                  description: invoiceItems.map(i => i.description).join(', '),
                  supplier: { name: selectedCustomer?.label || '—' },
                  items: invoiceItems,
                  // use totals from jsonPayload
                  sub_total: jsonPayload.sub_total,
                  total_discount: jsonPayload.total_discount,
                  total_vat: jsonPayload.total_vat,
                  total_wht: jsonPayload.total_wht,
                  total_price: jsonPayload.total_price,
                  total: jsonPayload.amount,
                  amount: jsonPayload.amount,
                  balance: jsonPayload.amount,
                  amount_paid: 0,
                }
              }
            });
          });
          return;
        } catch (jsonErr) {
          console.error('JSON retry failed:', jsonErr.response || jsonErr.message || jsonErr);
          try { console.error('JSON retry response body:', JSON.stringify(jsonErr.response?.data, null, 2)); } catch (e) { console.error('JSON retry response (raw):', jsonErr.response?.data); }
          // fall through to show original message
        }
      }

      if (serverData && serverData.message) {
        // Prefer a clean string message for the user (no JSON quotes/formatting)
        let msg = serverData.message;
        if (typeof msg !== 'string') {
          try {
            msg = JSON.stringify(msg);
          } catch {
            msg = String(msg);
          }
        }
        swal('Error', msg, 'error');
      } else if (error?.code === 'ECONNABORTED') {
        swal('Error', 'Request timeout - the server took too long to respond. Please check your connection and try again.', 'error');
      } else if (error?.code === 'ERR_NETWORK' || !error.response) {
        swal('Error', 'Network error - unable to connect to the server. Please check your internet connection and try again.', 'error');
      } else if (error?.response?.status === 401) {
        swal('Error', 'Session expired - please login again.', 'error');
      } else if (error?.response?.status === 403) {
        swal('Error', 'Permission denied - you do not have access to perform this action.', 'error');
      } else if (error?.response?.status === 500) {
        swal('Error', 'Server error - please contact support if the problem persists.', 'error');
      } else if (error?.message) {
        swal('Error', `Failed to save invoice: ${error.message}`, 'error');
      } else {
        swal('Error', 'Failed to save invoice - unknown error occurred.', 'error');
      }
    } finally {
      if (isMounted.current) {
        setIsSubmitting(false);
      }
    } // end try/catch/finally
   };

  return (
    <div>
      {!embedded && <HeaderNav activePage={pageMap[location.pathname]} />}
      <SupplierModal show={showSupplierModal} onHide={() => { setShowSupplierModal(false); fetchCustomers(); }} />
      <div className={`${classes.Container} ${embedded ? classes.embeddedContainer : ''}`}>
        {!embedded && (
          <header className={classes.Header}>
            <h2 className={classes.Title}>{isEditMode ? "Edit Purchase Invoice" : "New Purchase Invoice"}</h2>
            <h1 className={classes.Titles}>Manage all your purchase invoices.</h1>
          </header>
        )}
        <form className={classes.form}>
          <div className={classes.group}>
            <label>Supplier</label>
            <Select
              options={[{ value: '__add_supplier__', label: '➕ Add Supplier' }, ...(customerOptions || [])]}
              value={selectedCustomer}
              onChange={(opt) => {
                if (!opt) { setSelectedCustomer(null); setSelectedCustomerId(null); return; }
                if (opt.value === '__add_supplier__') {
                  // open supplier modal
                  setShowSupplierModal(true);
                  return;
                }
                setSelectedCustomer(opt);
                setSelectedCustomerId(opt?.id || opt?.value || null);
              }}
              placeholder="Choose supplier"
              isClearable
              classNamePrefix="react-select"
              required
            />
          </div>
          {/* Account moved into table per-row */}
          <div className={classes.group1}>
            <label>Date</label>
            <input
              type="date"
              value={invoiceDate}
              onChange={e => setInvoiceDate(e.target.value)}
              required
            />
          </div>
          <div className={classes.group1}>
            <label>Due Date</label>
            <input
              type="date"
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
              required
            />
          </div>
          <div className={classes.group1}>
            <label>Invoice Number</label>
            <input
              type="text"
              value={invoiceCode}
              onChange={e => setInvoiceCode(e.target.value)}
              placeholder="Invoice number"
            />
          </div>
          <div className={classes.group2}>
            <label>Currency</label>
            <Select
              options={currencyOptions}
              value={selectedCurrency}
              onChange={setSelectedCurrency}
              placeholder="Choose currency"
              isClearable
              classNamePrefix="react-select"
              required
            />

          </div>

        </form>

        <div className={classes.invoicecont}>
          {/* Toggle Buttons */}
          <div style={{ marginBottom: "1rem" }}>
            <button
              type="button"
              className={type === "products" ? classes.activeTab : classes.inactiveTab}
              onClick={() => setType("products")}
            >
              Products/Services
            </button>
            {/* <button
              type="button"
              className={type === "services" ? classes.activeTab : classes.inactiveTab}
              onClick={() => setType("services")}
            >
              Services
            </button> */}
          </div>
          <table className={`${classes.invoiceTable} ${actionStyles.invoiceTable}`}>
          <colgroup>
            {type === 'products' ? (
              <>
                {/* Product */}
                <col style={{ width: '220px' }} />
                {/* Account (products view can stay compact) */}
                <col style={{ width: '120px' }} />
                {/* Description */}
                <col style={{ width: '18%' }} />
                {/* Qty */}
                <col style={{ width: '80px' }} />
                {/* Price/Rate */}
                <col style={{ width: '120px' }} />
                {/* Discount (keep default width for products) */}
                <col style={{ width: '120px' }} />
                {/* VAT */}
                <col style={{ width: '70px' }} />
                {/* WHT */}
                <col style={{ width: '70px' }} />
                {/* VAT Amount */}
                <col style={{ width: '90px' }} />
                {/* Total */}
                <col style={{ width: '150px' }} />
                {/* Actions */}
                <col style={{ width: '60px' }} />
              </>
            ) : (
              <>
                {/* SERVICES VIEW ONLY */}
                {/* Account – widened to give more room */}
                {/* <col style={{ width: '200px' }} /> */}
                {/* Description */}
                <col style={{ width: '18%' }} />
                {/* Qty */}
                <col style={{ width: '20px !important' }} />
                {/* Price/Rate */}
                <col style={{ width: '120px' }} />
                {/* Discount – narrow, enough for 3–4 digits */}
                <col style={{ width: '20px !important' }} />
                {/* VAT */}
                <col style={{ width: '70px' }} />
                {/* WHT */}
                <col style={{ width: '70px' }} />
                {/* VAT Amount */}
                <col style={{ width: '90px' }} />
                {/* Total */}
                <col style={{ width: '150px' }} />
                {/* Actions */}
                <col style={{ width: '60px' }} />
              </>
            )}
          </colgroup>
          <thead>
            <tr>
              {/* Product / Service */}
              {type === 'products' && (
                <th className={classes.productservice}>Product/Service</th>
              )}
              <th>Account</th>
              <th className={classes.deschdInput}>Description</th>
              {/* Qty AFTER description, as requested */}
              <th className={classes.qtyhdInput}>Qty</th>
              <th className={classes.priceRateCol}>Price/Rate</th>
              <th className={classes.qtyhdInput}>Discount</th>
              <th className={classes.qtyhdInput}>VAT</th>
              <th className={classes.qtyhdInput}>WHT</th>
              <th>VAT Amount</th>
              <th>Total</th>
              <th className={classes.plushd}>
                <button type="button" className={classes.addRowBtn} onClick={handleAddRow}>+</button>
              </th>
            </tr>
          </thead>
            <tbody>
              {invoiceItems.length === 0 ? (
                <tr><td colSpan={type === 'products' ? 11 : 10}>{type === "products" ? (
                  <div>
                    {products.length === 0 ? "Loading products..." : (
                      <ul>
                        {products.map((p, i) => (
                          <li key={i}>{p.name} - ₦{p.price}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                ) : (
                  <div>
                    {services.length === 0 ? "Loading services..." : (
                      <ul>
                        {services.map((s, i) => (
                          <li key={i}>{s.name} - ₦{s.price}</li>
                        ))}
                      </ul>
                    )}
                  </div>
                )}
                </td></tr>
              ) : (
              invoiceItems.map((item, index) => (
                <tr key={index}>
                    {type === 'products' && (
                      <td className={actionStyles.productCell}>
                        <Select
                          options={productOptions}
                          value={productOptions.find(opt => opt.value === item.ledger) || null}
                            onChange={selected => {
                            const updatedItems = [...invoiceItems];
                            if (selected) {
                              updatedItems[index] = {
                                ...updatedItems[index],
                                ledger: selected.value,
                                description: selected.label,
                                price: selected.price || 0,
                              };
                            } else {
                              updatedItems[index] = {
                                ...updatedItems[index],
                                ledger: '',
                                description: '',
                                price: 0,
                              };
                            }
                            // reuse handleChange-style logic to keep VAT consistent
                            const it = updatedItems[index];
                            const qtyNumber = Number(it.qty) || 0;
                            const priceNumber = Number(it.price) || 0;
                            const baseTotal = round2(qtyNumber * priceNumber);
                            if (it.discountRaw && String(it.discountRaw).trim().endsWith('%')) {
                              const p = parseFloat(String(it.discountRaw).trim().slice(0, -1));
                              const pct = isNaN(p) ? 0 : p;
                              it.discountAmount = round2((baseTotal * pct) / 100);
                            }
                            const discount = Number(it.discountAmount) || 0;
                            const taxRate = it.vatChecked ? 0.075 : 0;
                            const vatAmount = round2((baseTotal - discount) * taxRate);
                            it.vat = vatAmount;
                            it.total = baseTotal - discount + vatAmount;
                            updatedItems[index] = it;
                            setInvoiceItems([...updatedItems]);
                          }}
                          placeholder={`Select product/service`}
                          isClearable
                          classNamePrefix={`react-select`}
                          className={classes.productSelect}
                        />
                      </td>
                    )}
                      <td>
                        <Select
                          options={accountOptions}
                          value={accountOptions.find(opt => String(opt.value) === String(item.account)) || null}
                          onChange={(sel) => {
                            const updated = [...invoiceItems];
                            updated[index] = { ...updated[index], account: sel ? sel.value : '' };
                            setInvoiceItems(updated);
                          }}
                          placeholder="Account"
                          isClearable
                          classNamePrefix="react-select"
                          className={classes.accountSelect}
                        />
                      </td>
                    <td className={actionStyles.descriptionCell}>
                      <input
                        type="text"
                        value={item.description}
                        className={`${classes.descriptionInput} ${actionStyles.descriptionCell}`}
                        onChange={(e) => handleChange(index, 'description', e.target.value)}
                      />
                    </td>
                    {/* Qty column, now AFTER description */}
                    <td>
                      <input
                        type="number"
                        min={1}
                        className={classes.qtyInput}
                        value={item.qty}
                        onChange={(e) => handleChange(index, 'qty', e.target.value)}
                      />
                    </td>
                    <td>
                      <input
                        type="text"
                        className={`${classes.priceRateInput} ${actionStyles.priceInput}`}
                        value={formatMoney(item.price?.toString() || "")}
                        onChange={(e) => { const cleaned = e.target.value.replace(/,/g, ''); handleChange(index, 'price', cleaned) }}
                      />
                    </td>
                      <td>
                        <input
                          type="text"
                          // allow "10%" or numeric amounts like "1500"
                          value={item.discountRaw || (item.discountAmount ? item.discountAmount.toString() : '')}
                          className={`${classes.dscntInput} ${actionStyles.discountInput}`}
                          onChange={(e) => handleChange(index, 'discount', e.target.value)}
                          placeholder="e.g. 10% or 1500"
                        />
                      </td>
                      <td>
                        <div className={classes.vatInput}>
                          <label style={{ whiteSpace: 'nowrap' }}>
                            <input
                              type="checkbox"
                              checked={Boolean(item.vatChecked)}
                              disabled={shouldDisableVatCheckbox(autoVatDeduction)}
                              onChange={(e) => handleChange(index, 'vatChecked', e.target.checked)}
                            />
                          </label>
                        </div>
                      </td>
                      <td>
                        <input
                          type="checkbox"
                          checked={item.wthChecked || false}
                          onChange={(e) => handleChange(index, 'wthChecked', e.target.checked)}
                        />
                      </td>
                      <td>
                        <input
                          className={`${classes.vatttInput} ${actionStyles.totalInput}`}
                          type="text"
                          value={formatMoney((Number(item.vat) || 0).toFixed(2))}
                          readOnly
                          style={{ textAlign: "right" }}
                        />
                      </td>
                      <td>
                        <input style={{ textAlign: "right" }} className={`${classes.vatttInput} ${actionStyles.totalInput}`} type="text" value={formatMoney(item.total.toFixed(2))} readOnly />
                      </td>
                      <td>
                        <Trash2
                          size={16}
                          onClick={() => handleDeleteRow(index)}
                          className={`${classes.deleteIcon} ${actionStyles.deleteIcon}`}
                        />
                      </td>
                    </tr>
                ))
              )}
            </tbody>
          </table>

          <div className={classes.paginationContainer}>
            <div className={classes.paginationControls}>
              <button>First</button>
              <button className={classes.active}>1</button>
              <button>2</button>
              <button>Last</button>
              <span className={classes.pageText}>Displaying 1 - {invoiceItems.length} of {invoiceItems.length}</span>
            </div>
          </div>


        </div>

        <div className={classes.foot}>
          <div className={classes.Footer}>
            <div className={`${classes.footerSection} ${classes.comments}`}>
              <label>Comments</label>
              <textarea className={classes.commentsBox}></textarea>
            </div>

            <div className={`${classes.footerSection} ${classes.policies}`}>
              <label>Policies and guidelines</label>
              <textarea className={classes.policiesBox}></textarea>
            </div>

            <div className={`${classes.footerSection} ${classes.summary}`}>
              <div className={classes.totals}>
                <div className={classes.totalRow}>
                  <span>Subtotal:</span>
                  <span>₦{formatMoney(subtotal.toFixed(2))}</span>
                </div>
                <div className={classes.totalRow}>
                  <span>VAT:</span>
                  <span>₦{formatMoney(totalVAT.toFixed(2))}</span>
                </div>
                <div className={classes.totalRow}>
                  <span>Discount:</span>
                  <span>
                    ₦{formatMoney(totalDiscount.toFixed(2))}
                    {discountPercentageOverall != null && discountPercentageOverall > 0 ? (
                      <span style={{ marginLeft: 8 }}>({discountPercentageOverall}% )</span>
                    ) : null}
                  </span>
                </div>
                <div className={classes.totalRow}>
                  <span>WHT:</span>
                  <span>₦{formatMoney(totalWHT.toFixed(2))}</span>
                </div>
                <div className={`${classes.totalRow} ${classes.total}`}>
                  <span>Total:</span>
                  <strong>₦{formatMoney(grandTotal.toFixed(2))}</strong>
                </div>
              </div>

              <div className={classes.actionButtons}>
                <button
                  className={classes.cancelButton}
                  type="button"
                  onClick={() => (onClose ? onClose() : navigate(-1))}
                >
                  Cancel
                </button>
                <button
                  className={classes.saveButton}
                  onClick={handleSaveInvoice}
                  type="button"
                  disabled={isSubmitting || isEditMode}
                >
                  {isSubmitting ? "Saving..." : isEditMode ? "Update unavailable" : "Save Invoice"}
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>



      {/* --- FOOTER Section (Untouched) --- */}


      {!embedded && <Footer />}
    </div>
  );
};

export default NewItem;
